import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { Language, Work } from "@mui/icons-material";
import { Avatar, Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { EmailWithCopyIcon } from "~ui/molecules/EmailWithCopyIcon/EmailWithCopyIcon";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { SalesOwnerAvatar } from "~ui/molecules/SalesOwnerAvatar/SalesOwnerAvatar";
import { PROSPECT_TYPES } from "~utils/constants/prospectTypes";
import { STAGES } from "~utils/constants/stages";
import { makeProfileAvatarUrl } from "~utils/url";

const tLastActivity = "Last activity";
const tOwner = "Owner";
const tSelectOwner = "Select owner";
const tStage = "Stage";
const tType = "Type";
const tUnknown = "Unknown";
const tContactInformation = "Contact Information";
const tGoToWebsite = "Go to website";

export interface ProspectOwnerOption {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
}

interface ProspectInfoProps {
  prospect: ProspectDto;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  primaryContact?: {
    firstName?: string;
    lastName?: string;
    roleTitle?: string;
  };
  onContactClick?: () => void;
  /** Sales people the current user may assign as owner. */
  ownerOptions?: ProspectOwnerOption[];
  /** When set, bubble admins can change ownership from the owner field. */
  onOwnerChange?: (userId: string) => void;
  isUpdatingOwner?: boolean;
}

export const ProspectInfo = ({
  prospect,
  primaryEmail,
  primaryPhone,
  primaryContact,
  onContactClick,
  ownerOptions = [],
  onOwnerChange,
  isUpdatingOwner = false,
}: ProspectInfoProps) => {
  const canChangeOwner = Boolean(onOwnerChange);
  const ownerSelectOptions = useMemo(() => {
    const options = [...ownerOptions];
    if (prospect.ownerUserId && !options.some((option) => option.id === prospect.ownerUserId)) {
      options.unshift({
        firstName: prospect.ownerFirstName,
        id: prospect.ownerUserId,
        lastName: prospect.ownerLastName,
      });
    }
    return options;
  }, [ownerOptions, prospect.ownerFirstName, prospect.ownerLastName, prospect.ownerUserId]);
  const hasOwnerName = Boolean(prospect?.ownerFirstName || prospect?.ownerLastName);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Basic Info Section */}
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h5">{prospect?.name ?? ""}</Typography>
        </Box>
        <Typography color="text.secondary" variant="body2">
          {tLastActivity} {DateTime.fromISO(prospect?.updatedAt ?? "").toRelative()}
        </Typography>
      </Box>

      {/* Owner */}
      {(canChangeOwner || hasOwnerName) && (
        <Box>
          {canChangeOwner ? (
            <FormControl fullWidth size="small">
              <InputLabel id="prospect-owner-label">{tOwner}</InputLabel>
              <Select
                disabled={isUpdatingOwner}
                displayEmpty
                label={tOwner}
                labelId="prospect-owner-label"
                onChange={(event) => {
                  const userId = event.target.value;
                  if (userId && userId !== prospect.ownerUserId) {
                    onOwnerChange?.(userId);
                  }
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography color="text.secondary" variant="body2">
                        {tSelectOwner}
                      </Typography>
                    );
                  }
                  const selectedOwner = ownerSelectOptions.find((option) => option.id === selected);
                  if (!selectedOwner) {
                    return selected;
                  }
                  return (
                    <SalesOwnerAvatar
                      firstName={selectedOwner.firstName}
                      lastName={selectedOwner.lastName}
                      showName
                      size="small"
                      userId={selectedOwner.id}
                    />
                  );
                }}
                value={prospect.ownerUserId ?? ""}
              >
                {ownerSelectOptions.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <SalesOwnerAvatar
                      firstName={user.firstName}
                      lastName={user.lastName}
                      showName
                      size="small"
                      userId={user.id}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <Typography variant="subtitle2" gutterBottom>
                {tOwner}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  alt={[prospect.ownerFirstName, prospect.ownerLastName].filter(Boolean).join(" ")}
                  src={prospect.ownerUserId ? makeProfileAvatarUrl(prospect.ownerUserId) : undefined}
                  sx={{ height: 24, width: 24 }}
                />
                <Typography>{[prospect.ownerFirstName, prospect.ownerLastName].filter(Boolean).join(" ")}</Typography>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Stage & Type */}
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tStage}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <span>{STAGES[prospect?.stage ?? 0].emoji}</span>
            <Typography>{STAGES[prospect?.stage ?? 0].name}</Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tType}
          </Typography>
          <Typography>{PROSPECT_TYPES.find((type) => type.id === prospect?.prospectType)?.name ?? tUnknown}</Typography>
        </Box>
      </Box>

      {/* Contact Information */}
      {(primaryEmail || primaryPhone || primaryContact) && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tContactInformation}
          </Typography>
          <Paper
            variant="outlined"
            onClick={onContactClick}
            sx={{
              "&:hover": onContactClick
                ? {
                    boxShadow: 1,
                  }
                : {},
              cursor: onContactClick ? "pointer" : "default",
              display: "grid",
              gap: 2,
              p: 2,
            }}
          >
            {/* Contact Name */}
            {primaryContact && (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">
                  {[primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(" ")}
                </Typography>
              </Box>
            )}

            {/* Role */}
            {primaryContact?.roleTitle && (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Work fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {primaryContact.roleTitle}
                </Typography>
              </Box>
            )}

            {/* Contact Info */}
            <Stack spacing={1}>
              {primaryEmail && <EmailWithCopyIcon email={primaryEmail} />}
              {primaryPhone && <PhoneWithCopyIcon phoneNumber={primaryPhone} />}
              {prospect?.website && (
                <Box
                  component="a"
                  href={prospect.website.startsWith("http") ? prospect.website : `https://${prospect.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    alignItems: "center",
                    color: "primary.main",
                    display: "flex",
                    gap: 0.5,
                    textDecoration: "none",
                  }}
                >
                  <Language fontSize="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {tGoToWebsite}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
};
